// Base Libs
import { useContext } from "react";
import { shallowEqual } from "react-redux";
import { GuxBadge, GuxIcon } from "genesys-spark-components-react";
import { SurveyQuestionTypeEnum } from "@genesys/cloud-frontend/dist/mjs/models";

// Styles
import "./ScorableQuestion.less";

// Base commons
import { useAppSelector } from "app/hooks";
import QuestionResult from "enums/QuestionResult";

// Feature commons
import { FormLocaleProvider } from "features/i18n/useLocale";
import { useStrings } from "features/i18n/useStrings";
import {
    HasEditScoreOrEditPermissionsContext,
    ScorableValidationContext,
} from "features/qualityForm/common/Contexts";
import QuestionBody from "features/qualityForm/common/questionComponents/QuestionBody";
import QuestionName from "features/qualityForm/common/questionComponents/QuestionName";
import { selectQuestionHelpText } from "features/qualityForm/qualityFormSlice";
import QuestionOperations from "features/qualityForm/common/questionComponents/QuestionOperations";
import { ErrorStatement } from "features/qualityForm/formEditor/common/ErrorStatement/ErrorStatement";
import {
    selectIsAdditionalCommentValid,
    selectIsNPSExplanationPresentIfRequired,
    selectIsScorableQuestionAnswered,
} from "features/scorable/ScorableFormValidations";
import { HelpButton } from "features/common/HelpButton";

// Feature slices
import {
    selectQuestionMaxScore,
    selectQuestionSettingsIndicators,
} from "features/qualityForm/qualityFormSlice";
import { selectQuestion } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    selectIsScorableComplete,
    selectQuestionScore,
} from "features/scorable/scorableSlice";
import "./ScorableQuestion.less";

// Locals
import { QuestionMessages } from "./Evaluation/QuestionMessages";
// Locales
import strings from "./ScorableQuestion.strings";

export default function ScorableQuestion({
    id,
    allowEditing = false,
    hideScore = false,
    canReviewResults = false,
}: {
    id: string;
    allowEditing?: boolean;
    hideScore?: boolean;
    canReviewResults?: boolean;
}) {
    const t = useStrings(strings);
    const questionType = useAppSelector((state) => {
        const question = selectQuestion(state, id);
        return question?.type;
    });

    const { hasEditScoreOrEditPermissions } = useContext(
        HasEditScoreOrEditPermissionsContext
    );
    const isScorableComplete = useAppSelector(selectIsScorableComplete);
    const isQuestionEditable =
        (allowEditing || !isScorableComplete) &&
        !(hasEditScoreOrEditPermissions === false);

    const { showValidationErrors } = useContext(ScorableValidationContext);

    const showErrorForNotAnswering = useAppSelector(
        (state) =>
            showValidationErrors && !selectIsScorableQuestionAnswered(state, id)
    );

    const comments = useAppSelector((state) => {
        const score = selectQuestionScore(state, id);
        return score?.comments;
    });
    const isAdditionalCommentInvalid = useAppSelector(
        (state) =>
            showValidationErrors && !selectIsAdditionalCommentValid(state, id)
    );
    const showNPSExplanationAbsentError = useAppSelector((state) => {
        if (questionType === SurveyQuestionTypeEnum.NpsQuestion) {
            return (
                showValidationErrors === true &&
                !selectIsNPSExplanationPresentIfRequired(state, id)
            );
        }
        return false;
    });

    const hasError = showErrorForNotAnswering || isAdditionalCommentInvalid;
    const { score, questionMaxScore, isCritical, isKill, markedNA } =
        useAppSelector((state) => {
            const { score, markedNA } = selectQuestionScore(state, id) ?? {
                score: 0,
                markedNA: false,
            };
            const questionMaxScore = selectQuestionMaxScore(state, id);
            const { isCritical, isKill } = selectQuestionSettingsIndicators(
                state,
                id
            );
            return {
                score,
                questionMaxScore,
                isCritical,
                isKill,
                markedNA,
            };
        }, shallowEqual);

    const helpText = useAppSelector((state) =>
        selectQuestionHelpText(state, id)
    );

    let questionResult;
    let resultBadgeColor;
    let resultBadgeIcon;
    let resultLabelMessage;

    if (score === questionMaxScore) {
        questionResult = QuestionResult.CORRECT;
    } else if (score === 0) {
        questionResult = QuestionResult.INCORRECT;
    } else {
        questionResult = QuestionResult.PARTIAL;
    }

    switch (questionResult) {
        case QuestionResult.CORRECT:
            resultBadgeColor = "success";
            resultBadgeIcon = "alert-success";
            resultLabelMessage = t.labels.correct;
            break;
        case QuestionResult.INCORRECT:
            resultBadgeColor = "error";
            resultBadgeIcon = "close-circle";
            resultLabelMessage = t.labels.incorrect;
            break;
        case QuestionResult.PARTIAL:
            resultBadgeColor = "warning";
            resultBadgeIcon = "alert-warning-triangle";
            resultLabelMessage = t.labels.partial;
            break;
        default:
            resultBadgeColor = "info";
            resultBadgeIcon = "";
            resultLabelMessage = "";
    }

    return (
        <div
            className="ScorableQuestion"
            data-invalid={hasError === true}
            data-testid="scorable-question"
        >
            <div className="question-header">
                <div className="QuestionName">
                    <QuestionName questionId={id} />
                </div>

                <div className="HelpButton">
                    <HelpButton helpText={helpText} />
                </div>
            </div>

            <QuestionMessages questionId={id} />
            <FormLocaleProvider>
                <QuestionBody
                    questionId={id}
                    isQuestionEditable={isQuestionEditable}
                    hideScore={hideScore}
                    canReviewResults={canReviewResults}
                >
                    {isQuestionEditable && (
                        <QuestionOperations questionId={id} />
                    )}
                    {!isQuestionEditable && (
                        <div className="comment-text">{comments}</div>
                    )}
                </QuestionBody>
            </FormLocaleProvider>
            {canReviewResults && (
                <div className="scorable-question-result">
                    {markedNA && (
                        <GuxBadge>
                            <GuxIcon
                                iconName="ban-outline"
                                decorative
                            ></GuxIcon>
                            {t.labels.markedNA}
                        </GuxBadge>
                    )}
                    {!markedNA && (
                        <GuxBadge accent={resultBadgeColor}>
                            {resultBadgeIcon && (
                                <GuxIcon
                                    iconName={resultBadgeIcon}
                                    decorative
                                ></GuxIcon>
                            )}
                            {resultLabelMessage}
                        </GuxBadge>
                    )}
                    {isKill && (
                        <GuxBadge accent="error">
                            {t.labels.fatalQuestion}
                        </GuxBadge>
                    )}
                    {!isKill && isCritical && (
                        <GuxBadge accent="inherit" class="fatal-question-badge">
                            {t.labels.criticalQuestion}
                        </GuxBadge>
                    )}
                </div>
            )}
            <div
                className="scorable-answer-option-error"
                data-testid="scorable-answer-option-error"
            >
                {showErrorForNotAnswering && (
                    <ErrorStatement errorMessage={t.errors.requiredQuestion} />
                )}
                {isAdditionalCommentInvalid && (
                    <ErrorStatement errorMessage={t.errors.requiredComment} />
                )}
                {showNPSExplanationAbsentError && (
                    <ErrorStatement
                        errorMessage={t.errors.explanationResponseEmpty}
                    />
                )}
            </div>
        </div>
    );
}
